// frontend/user/src/components/file-view/details-card.tsx
// 文件信息卡(从 routes/file-preview.tsx 抽出)。按媒体类型渲染不同字段;非媒体走通用卡。
// 详情页 / 预览页共用。InfoRow 仅此文件用,内联定义。
// 设计稿(dogdisk-user.pen KJLF5):surface 色卡片,iOS 行式(17px / space-between / 无分隔线)。
//   卡片无可见标题;标题文案以 sr-only 保留(供 e2e `-info` 文案断言 + a11y)。
// 横向内边距由调用方(px-5 包裹)负责,本卡不再自带 mx-5,避免与父层双重缩进。

import { formatBytes } from '@dogdisk/shared'
import {
  fmtBitrate,
  fmtDuration,
  fmtResolution,
  type AudioInfo,
  type ImageInfo,
  type VideoInfo,
} from './use-file-meta'

export type FileViewKind = 'image' | 'video' | 'audio' | 'other'

// KJLF5: cornerRadius 16(rounded-2xl)/ fill $surface-color / padding 16 / 行间 gap 12。
const CARD_CLASS = 'flex flex-col gap-3 rounded-2xl bg-[#F6F7F8] p-4'

export function DetailsCard({
  frameId,
  kind,
  mime,
  size,
  createdAt,
  imageInfo,
  videoInfo,
  audioInfo,
}: {
  frameId: string
  kind: FileViewKind
  mime: string
  size: number | null
  createdAt: string | undefined
  imageInfo?: ImageInfo | null
  videoInfo?: VideoInfo | null
  audioInfo?: AudioInfo | null
}) {
  if (kind === 'image') {
    return (
      <section data-testid={`${frameId}-info`} className={CARD_CLASS}>
        <p className="sr-only">文件信息</p>
        <InfoRow label="类型" value={mime || '—'} />
        <InfoRow label="大小" value={size != null ? formatBytes(size) : '—'} />
        <InfoRow label="分辨率" value={fmtResolution(imageInfo?.width, imageInfo?.height)} />
        <InfoRow label="拍摄时间" value={createdAt?.slice(0, 10) ?? '—'} />
      </section>
    )
  }
  if (kind === 'video') {
    return (
      <section data-testid={`${frameId}-info`} className={CARD_CLASS}>
        <p className="sr-only">视频信息</p>
        <InfoRow label="时长" value={fmtDuration(videoInfo?.duration_ms)} />
        <InfoRow label="分辨率" value={fmtResolution(videoInfo?.width, videoInfo?.height)} />
        <InfoRow label="编码" value={videoInfo?.video_codec ?? '—'} />
      </section>
    )
  }
  if (kind === 'audio') {
    return (
      <section data-testid={`${frameId}-info`} className={CARD_CLASS}>
        <p className="sr-only">音频信息</p>
        <InfoRow label="时长" value={fmtDuration(audioInfo?.duration_ms)} />
        <InfoRow label="码率" value={fmtBitrate(audioInfo?.bitrate_bps)} />
        <InfoRow label="编码" value={audioInfo?.codec ?? '—'} />
      </section>
    )
  }
  // other(非媒体)通用信息卡
  return (
    <section data-testid={`${frameId}-info`} className={CARD_CLASS}>
      <InfoRow label="文件类型" value={mime || '未知'} />
      <InfoRow label="文件大小" value={size != null ? formatBytes(size) : '—'} />
      <InfoRow label="修改时间" value={createdAt?.slice(0, 16).replace('T', ' ') ?? '—'} />
      <InfoRow label="存储位置" value="我的网盘/工作" />
    </section>
  )
}

// detailRow(KJLF5/qe3oo…):space-between,label/value 同 17px;label normal·$text-secondary,value 500·$text-primary。
function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="shrink-0 text-[17px] text-[#9CA3AF]">{label}</span>
      <span className="truncate text-right text-[17px] font-medium text-[#1A1A1A]">{value}</span>
    </div>
  )
}
